import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  Users,
  Wallet,
  Coins,
  Repeat,
  ArrowDownToLine,
  Send,
  Ticket,
  ShoppingCart,
  Package,
  List,
  FileText,
  Trophy,
  Sprout,
  Handshake,
  Sparkles,
  Landmark,
  LifeBuoy,
  Database,
  Settings,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  Palette,
  Boxes,
  Gift,
  ArrowRightLeft,
  TrendingUp,
  Shield,
  TrendingDown,
  Megaphone,
  Store,
  Home,
  DollarSign,
  Link as LinkIcon,
  MessageSquare,
  Cog,
  ChevronDown,
  ChevronRight,
  Truck,
  AlertTriangle,
  Sliders
} from 'lucide-react';
import { toast } from 'sonner';

const navigationCategories = [
  {
    category: 'Home & Overview',
    icon: Home,
    items: [
      { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
      { name: 'Landing Page', href: '/admin/landing', icon: Palette },
    ]
  },
  {
    category: 'Treasury & Finance',
    icon: DollarSign,
    items: [
      { name: 'Treasury', href: '/admin/treasury', icon: Landmark },
      { name: 'Treasury Wallets', href: '/admin/treasury/wallets', icon: Shield },
      { name: 'Asset Consolidation', href: '/admin/consolidation', icon: ArrowRightLeft },
      { name: 'Auto-Withdrawals', href: '/admin/auto-withdrawals', icon: ArrowRightLeft },
      { name: 'Withdrawals', href: '/admin/withdrawals', icon: TrendingDown },
      { name: 'Deposits', href: '/admin/deposits', icon: ArrowDownToLine },
      { name: 'Growth Packages', href: '/admin/growth-packages', icon: TrendingUp },
    ]
  },
  {
    category: 'Blockchain & Wallets',
    icon: LinkIcon,
    items: [
      { name: 'Custodial Wallets', href: '/admin/custodial-wallets', icon: Wallet },
      { name: 'Blockchain Wallets', href: '/admin/blockchain-wallets', icon: Boxes },
      { name: 'Asset Registry', href: '/admin/blockchain/assets', icon: Database },
      { name: 'Wallets', href: '/admin/wallets', icon: Wallet },
    ]
  },
  {
    category: 'Commerce & Marketplace',
    icon: Store,
    items: [
      { name: 'Marketplace', href: '/admin/marketplace', icon: Store },
      { name: 'Commerce Fees', href: '/admin/commerce', icon: ShoppingCart },
      { name: 'Shop', href: '/admin/shop', icon: ShoppingCart },
      { name: 'Orders', href: '/admin/orders', icon: Package },
      { name: 'Marketing & Ads', href: '/admin/marketing/ads', icon: Megaphone },
      { name: 'Auto Ads', href: '/admin/auto-ads', icon: Sparkles },
      { name: 'Matrise Runners', href: '/admin/pickup', icon: Truck },
      { name: 'Runner Disputes', href: '/admin/disputes', icon: AlertTriangle },
    ]
  },
  {
    category: 'Exchange & Currency',
    icon: Repeat,
    items: [
      { name: 'Exchange Dashboard', href: '/admin/exchange-dashboard', icon: TrendingUp },
      { name: 'Currency Management', href: '/admin/exchange-management', icon: ArrowRightLeft },
      { name: 'Currencies', href: '/admin/currencies', icon: Coins },
      { name: 'Exchange Rates', href: '/admin/exchange-rates', icon: Repeat },
    ]
  },
  {
    category: 'Users & Agents',
    icon: Users,
    items: [
      { name: 'Users', href: '/admin/users', icon: Users },
      { name: 'Agent Network', href: '/admin/agents', icon: Handshake },
      { name: 'Referrals', href: '/admin/referrals', icon: Gift },
      { name: 'Vouchers', href: '/admin/vouchers', icon: Ticket },
    ]
  },
  {
    category: 'Features & Community',
    icon: MessageSquare,
    items: [
      { name: 'Matrise Connect Admin', href: '/admin/connect', icon: Megaphone },
      { name: 'Community', href: '/admin/groups', icon: Handshake },
      { name: 'Rewards', href: '/admin/tasking', icon: Sparkles },
      { name: 'Savings', href: '/admin/savings', icon: Sprout },
      { name: 'Pool Predictions', href: '/admin/pool-predictions', icon: Trophy },
      { name: 'Predictions (Legacy)', href: '/admin/predictions', icon: Trophy },
    ]
  },
  {
    category: 'System & Settings',
    icon: Cog,
    items: [
      { name: 'Feature Controls', href: '/admin/feature-controls', icon: Sliders },
      { name: 'Transactions', href: '/admin/transactions', icon: List },
      { name: 'Audit Logs', href: '/admin/audit-logs', icon: FileText },
      { name: 'Deposit Methods', href: '/admin/deposit-methods', icon: Landmark },
      { name: 'Support', href: '/admin/support', icon: LifeBuoy },
      { name: 'Backup', href: '/admin/backup', icon: Database },
      { name: 'Settings', href: '/admin/settings', icon: Settings },
    ]
  },
];

export const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(
    navigationCategories.reduce((acc, cat) => {
      acc[cat.category] = true; // Start with all categories expanded
      return acc;
    }, {})
  );

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow border-r border-border bg-surface pt-5 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6">
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-logo">
              Matrise Admin
            </h1>
          </div>
          <div className="mt-8 flex-1 flex flex-col">
            <nav className="flex-1 px-3 space-y-1">
              {navigationCategories.map((category) => {
                const CategoryIcon = category.icon;
                const isExpanded = expandedCategories[category.category];
                const hasActiveItem = category.items.some(item => location.pathname === item.href);
                
                return (
                  <div key={category.category} className="mb-2">
                    <button
                      onClick={() => toggleCategory(category.category)}
                      className={`w-full group flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                        hasActiveItem
                          ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`admin-category-${category.category.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <div className="flex items-center">
                        <CategoryIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                        {category.category}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="mt-1 ml-4 space-y-0.5">
                        {category.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.href;
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              data-testid={`admin-nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                                isActive
                                  ? 'bg-blue-600 text-white'
                                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                            >
                              <Icon className={`mr-3 h-4 w-4 flex-shrink-0 ${
                                isActive ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'
                              }`} />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-border p-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0) || 'A'}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-foreground">{user?.name || user?.email}</p>
                  <p className="text-xs text-muted-foreground">Administrator</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  data-testid="admin-theme-toggle"
                  className="h-8 w-8"
                >
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="admin-logout-button"
                  className="h-8 w-8 text-red-600 hover:text-red-700"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="md:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-surface border-b border-border md:hidden">
          <button
            type="button"
            className="px-4 text-muted-foreground focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="admin-mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="flex-1 flex justify-between items-center px-4">
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-8 w-8"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-surface border-b border-border">
            <nav className="px-2 py-2 space-y-1 max-h-[80vh] overflow-y-auto">
              {navigationCategories.map((category) => {
                const CategoryIcon = category.icon;
                const isExpanded = expandedCategories[category.category];
                const hasActiveItem = category.items.some(item => location.pathname === item.href);
                
                return (
                  <div key={category.category} className="mb-2">
                    <button
                      onClick={() => toggleCategory(category.category)}
                      className={`w-full group flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-lg ${
                        hasActiveItem
                          ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <div className="flex items-center">
                        <CategoryIcon className="mr-3 h-5 w-5" />
                        {category.category}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="mt-1 ml-4 space-y-0.5">
                        {category.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.href;
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                                isActive
                                  ? 'bg-blue-600 text-white'
                                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                            >
                              <Icon className="mr-3 h-4 w-4" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                onClick={handleLogout}
                className="w-full group flex items-center px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg mt-4"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </button>
            </nav>
          </div>
        )}

        <main className="flex-1 p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
